<script>
import NextButton from 'dashboard/components-next/button/Button.vue';
import ContactAPI from 'dashboard/api/contacts';
import CrmDealsAPI from 'dashboard/api/crmDeals';
import { useAlert } from 'dashboard/composables';
import DealCustomField from './DealCustomField.vue';
import DealDocuments from './DealDocuments.vue';
import {
  ACTIVITY_EMOJI,
  ACTIVITY_LABEL,
  ACTIVITY_TYPES,
  NOTE_TYPE,
  SCHEDULABLE_TYPES,
} from './helpers/activityTypes';

export default {
  name: 'DealDrawerContent',
  components: { NextButton, DealCustomField, DealDocuments },
  props: {
    dealId: { type: [Number, String], required: true },
    // Embutido na aba "Negócio" do detalhe da conversa: lá o cabeçalho do
    // acordeão já diz o que é, e não existe drawer pra fechar.
    embedded: { type: Boolean, default: false },
  },
  emits: ['close', 'deleted'],
  data() {
    return {
      activeTab: 'details',
      confirmingDelete: false,
      isDeleting: false,
      editingContact: false,
      editingClosedAt: false,
      closedAtDraft: '',
      contactQuery: '',
      contactResults: [],
      isSearchingContact: false,
      contactSearchTimer: null,
      targetStatus: null,
      selectedPaymentMethodId: '',
      selectedLostReasonId: '',
      selectedAssigneeId: '',
      showTaskForm: false,
      editingTaskId: null,
      newTask: {
        title: '',
        dueAt: '',
        description: '',
        activityType: 'follow_up',
        outcome: '',
      },
      showMoreFields: false,
      // 'schedule' marca compromisso pro futuro; 'log' registra o que já
      // aconteceu, que é o outro metade do que uma atividade precisa cobrir.
      taskFormMode: 'schedule',
      installmentCount: 1,
      firstInstallmentDueDate: new Date().toISOString().slice(0, 10),
      selectedProductId: '',
      newLineQuantity: 1,
    };
  },
  computed: {
    deal() {
      return this.$store.getters['crmDeals/getCurrentDeal'];
    },
    isCurrentUserAdmin() {
      return this.$store.getters.getCurrentRole === 'administrator';
    },
    // O input de data quer YYYY-MM-DD; closed_at chega como timestamp ISO.
    closedAtDate() {
      if (!this.deal?.closed_at) return '';
      const d = new Date(this.deal.closed_at);
      return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
    },
    uiFlags() {
      return this.$store.getters['crmDeals/getUIFlags'];
    },
    isLoading() {
      return this.uiFlags.isFetchingDeal && !this.deal;
    },
    funnels() {
      return this.$store.getters['crmDeals/getFunnels'];
    },
    currentFunnel() {
      if (!this.deal) return null;
      return (
        this.funnels.find(f => String(f.id) === String(this.deal.funnel_id)) ||
        null
      );
    },
    stages() {
      return this.currentFunnel?.stages || [];
    },
    currentStage() {
      if (!this.deal) return null;
      return this.stages.find(s => s.id === this.deal.stage_id) || null;
    },
    origins() {
      return this.$store.getters['crmDeals/getOrigins'];
    },
    paymentMethods() {
      return this.$store.getters['crmDeals/getPaymentMethods'];
    },
    lostReasons() {
      return this.$store.getters['crmDeals/getLostReasons'];
    },
    assignee() {
      if (!this.deal?.assignee_id) return null;
      return this.$store.getters['agents/getAgentById'](this.deal.assignee_id);
    },
    agents() {
      return this.$store.getters['agents/getAgents'];
    },
    teams() {
      return this.$store.getters['teams/getTeams'];
    },
    sourceDeal() {
      if (!this.deal?.source_deal_id) return null;
      return {
        id: this.deal.source_deal_id,
        title: this.deal.source_deal_title,
      };
    },
    tasks() {
      return this.deal?.tasks || [];
    },
    // Campos personalizados do negócio, definidos em Configurações. Valem pra
    // conta inteira, então todo negócio de todo funil vê a mesma lista.
    // O que a etapa ATUAL exige e ainda não foi cumprido. Aparece antes de a
    // pessoa tentar mover e tomar um "não" sem explicação.
    // O campo era `type="number"`, e por isso mostrava 7500,0. Dinheiro se lê
    // em pt-BR (7.500,00), então o input virou texto: formata pra exibir e
    // desmonta pra gravar. COMPUTED, não método: bindar a função sem chamar
    // colocava "function () { [native code] }" dentro do campo.
    formattedDealValue() {
      const value = Number(this.deal?.value || 0);
      // A moeda entra no próprio texto formatado, e não num símbolo posicionado
      // por cima do campo: sobreposto, o "R$" comia o primeiro dígito.
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(value);
    },
    pendingRequirements() {
      return this.deal?.pending_requirements || [];
    },
    stageChecklist() {
      return this.deal?.checklist || [];
    },
    // Requisitos do FUNIL para marcar como ganho (além dos da etapa).
    pendingWinRequirements() {
      return this.deal?.pending_win_requirements || [];
    },
    winChecklist() {
      return this.deal?.win_checklist || [];
    },
    showWinBlock() {
      if (this.deal?.status !== 'open') return false;
      return (
        this.pendingWinRequirements.length > 0 || this.winChecklist.length > 0
      );
    },
    dealAttributeDefinitions() {
      const all = this.$store.getters['attributes/getAttributes'] || [];
      return all.filter(
        attribute => attribute.attribute_model === 'deal_attribute'
      );
    },
    // Campo marcado como sempre visível fica na coluna; o resto desce pro
    // expansor. Sem essa divisão, uma conta com vinte campos transformava a
    // aba Detalhes numa parede de rolagem.
    requiredDealKeys() {
      return (this.deal?.stage_requirements?.fields || []).filter(key =>
        String(key).startsWith('custom:')
      );
    },
    // Campo exigido pela etapa nunca fica no expansor: seria pedir pra
    // preencher algo que a pessoa não está vendo.
    isRequiredDealAttribute() {
      return definition =>
        this.requiredDealKeys.includes(`custom:${definition.attribute_key}`);
    },
    visibleDealAttributes() {
      return this.dealAttributeDefinitions.filter(
        definition =>
          definition.always_visible !== false ||
          this.isRequiredDealAttribute(definition)
      );
    },
    collapsedDealAttributes() {
      return this.dealAttributeDefinitions.filter(
        definition =>
          definition.always_visible === false &&
          !this.isRequiredDealAttribute(definition)
      );
    },
    // Campos exigidos que moram no CONTATO. A gaveta não os tinha, então sem
    // isto a pessoa lia "falta Cidade" e não tinha onde preencher.
    pendingContactFields() {
      return this.deal?.pending_contact_fields || [];
    },
    installments() {
      return this.deal?.installments || [];
    },
    productLines() {
      return this.deal?.product_lines || [];
    },
    products() {
      return this.$store.getters['crmDeals/getProducts'];
    },
    availableProducts() {
      const usedIds = this.productLines.map(l => l.product_id);
      return this.products.filter(p => !usedIds.includes(p.id));
    },
    receivable() {
      const total = parseFloat(this.deal?.value) || 0;
      const paid = parseFloat(this.deal?.paid_value) || 0;
      return Math.max(0, total - paid);
    },
    overdueTasks() {
      return this.tasks.filter(
        t => !t.completed_at && t.due_at && new Date(t.due_at) < new Date()
      );
    },
    drawerTabs() {
      return [
        { id: 'details', label: 'Detalhes', badge: 0 },
        {
          id: 'tasks',
          label: 'Atividades',
          badge: this.overdueTasks.length || 0,
        },
        { id: 'products', label: 'Produtos', badge: 0 },
        {
          id: 'documents',
          label: 'Documentos',
          badge: this.deal?.documents_count || 0,
        },
      ];
    },
  },
  watch: {
    dealId: {
      immediate: true,
      handler() {
        this.targetStatus = null;
        this.selectedPaymentMethodId = '';
        this.selectedLostReasonId = '';
        this.confirmingDelete = false;
        this.loadDeal();
      },
    },
  },
  methods: {
    loadDeal() {
      if (!this.dealId) return;
      this.$store.dispatch('crmDeals/fetchDeal', this.dealId);
      // Fora do quadro ninguém carregou os funis, e sem eles os selects de
      // Funil e Etapa ficam vazios.
      if (!this.funnels.length) this.$store.dispatch('crmDeals/fetchFunnels');
      this.$store.dispatch('crmDeals/fetchOrigins');
      this.$store.dispatch('crmDeals/fetchPaymentMethods');
      this.$store.dispatch('crmDeals/fetchLostReasons');
      this.$store.dispatch('crmDeals/fetchProducts');
      this.$store.dispatch('teams/get');
    },
    // O erro precisa aparecer E o campo precisa voltar ao que está gravado.
    // Sem isso, um requisito de etapa barrava a troca no seletor mas a tela
    // continuava exibindo a etapa nova, como se tivesse salvado.
    async saveDealField(field, value) {
      try {
        await this.$store.dispatch('crmDeals/updateDeal', {
          id: this.dealId,
          deal: { [field]: value },
        });
      } catch (error) {
        useAlert(
          error?.response?.data?.message ||
            'Não foi possível salvar a alteração'
        );
        await this.$store.dispatch('crmDeals/fetchDeal', this.dealId);
      }
    },
    // O jsonb inteiro viaja a cada mexida: o backend substitui a coluna, então
    // mandar só a chave alterada apagaria as outras.
    saveCustomAttribute(key, rawValue) {
      const current = { ...(this.deal?.custom_attributes || {}) };
      const value = rawValue === '' ? null : rawValue;

      if (value === null) {
        delete current[key];
      } else {
        current[key] = value;
      }

      this.$store.dispatch('crmDeals/updateDeal', {
        id: this.dealId,
        deal: { custom_attributes: current },
      });
    },
    onCustomAttributeChange({ key, value }) {
      this.saveCustomAttribute(key, value);
    },
    customAttributeValue(definition) {
      const value = this.deal?.custom_attributes?.[definition.attribute_key];
      return value === undefined || value === null ? '' : value;
    },

    onFieldChange(field, event) {
      this.saveDealField(field, event.target.value);
    },
    startEditingClosedAt() {
      this.closedAtDraft = this.closedAtDate;
      this.editingClosedAt = true;
    },
    // A venda nem sempre é marcada no dia em que aconteceu, e o relatório de
    // fechamentos conta por esta data. Só o dia muda; a hora fica a que era.
    // Salva só no Enter ou no botão: o evento `change` do input de data
    // dispara a cada dígito que forma uma data válida (o "2" do ano já vira
    // ano 0002), o que fechava o campo no meio da digitação.
    saveClosedAt() {
      const [y, m, d] = this.closedAtDraft.split('-').map(Number);
      if (!y || y < 2000) return;
      const base = this.deal.closed_at
        ? new Date(this.deal.closed_at)
        : new Date();
      const next = new Date(base);
      next.setFullYear(y, m - 1, d);
      this.saveDealField('closed_at', next.toISOString());
      this.editingClosedAt = false;
    },
    // Funil e etapa viajam juntos: o modelo recusa etapa que não pertence ao
    // funil, então trocar só um dos dois seria sempre inválido. Ao mudar de
    // funil o negócio entra na primeira etapa dele (as etapas vêm ordenadas
    // por posição do serializer).
    onFunnelChange(event) {
      const funnelId = event.target.value;
      const funnel = this.funnels.find(f => String(f.id) === String(funnelId));
      const firstStage = funnel?.stages?.[0];
      if (!firstStage) return;

      this.$store.dispatch('crmDeals/updateDeal', {
        id: this.dealId,
        deal: { funnel_id: funnelId, stage_id: firstStage.id },
      });
    },
    async deleteDeal() {
      this.isDeleting = true;
      try {
        await this.$store.dispatch('crmDeals/deleteDeal', this.dealId);
        this.confirmingDelete = false;
        // Quem hospeda decide o que fazer: o quadro fecha o drawer e recarrega,
        // a aba da conversa recarrega a lista do contato.
        this.$emit('deleted', this.dealId);
      } finally {
        this.isDeleting = false;
      }
    },
    startContactEdit() {
      this.editingContact = true;
      this.contactQuery = '';
      this.contactResults = [];
    },
    cancelContactEdit() {
      this.editingContact = false;
      this.contactQuery = '';
      this.contactResults = [];
    },
    // Mesma busca com debounce do AddNegotiationModal: a API de contatos é
    // paga por chamada em conta grande, então não dispara a cada tecla.
    onContactQueryInput() {
      clearTimeout(this.contactSearchTimer);
      const query = (this.contactQuery || '').trim();
      if (query.length < 2) {
        this.contactResults = [];
        return;
      }
      this.contactSearchTimer = setTimeout(async () => {
        this.isSearchingContact = true;
        try {
          const { data } = await ContactAPI.search(encodeURIComponent(query));
          this.contactResults = data.payload || [];
        } catch {
          this.contactResults = [];
        } finally {
          this.isSearchingContact = false;
        }
      }, 300);
    },
    selectContact(contact) {
      this.saveDealField('contact_id', contact.id);
      this.cancelContactEdit();
    },
    startStatusChange(status) {
      // Clicar de novo no status atual reabre o negócio.
      if (this.deal.status === status) {
        this.saveDealField('status', 'open');
        this.targetStatus = null;
        return;
      }
      this.targetStatus = status;
      this.selectedPaymentMethodId = this.deal.payment_method_id || '';
      this.selectedLostReasonId = this.deal.lost_reason_id || '';
      // Já tem responsável? mantém. Senão assume quem está operando — que é o
      // caso comum, e continua trocável no select.
      this.selectedAssigneeId =
        this.deal.assignee_id || this.$store.getters.getCurrentUserID || '';
    },
    cancelStatusChange() {
      this.targetStatus = null;
    },
    confirmStatusChange() {
      const deal = { status: this.targetStatus };
      if (this.targetStatus === 'won') {
        deal.payment_method_id = this.selectedPaymentMethodId;
      } else if (this.targetStatus === 'lost') {
        deal.lost_reason_id = this.selectedLostReasonId;
      }
      // String vazia viraria 0 no backend; nulo é "sem responsável".
      deal.assignee_id = this.selectedAssigneeId || null;
      this.$store.dispatch('crmDeals/updateDeal', { id: this.dealId, deal });
      this.targetStatus = null;
    },
    // Aceita "7.500,00", "7500,00", "7500" e "R$ 7.500,00". A vírgula manda
    // quando ela vem depois do último ponto: aí o ponto é separador de milhar.
    parseCurrency(text) {
      const raw = String(text).replace(/[^\d,.-]/g, '');
      if (!raw) return 0;

      const normalized =
        raw.lastIndexOf(',') > raw.lastIndexOf('.')
          ? raw.replace(/\./g, '').replace(',', '.')
          : raw.replace(/,/g, '');

      const parsed = Number.parseFloat(normalized);
      return Number.isNaN(parsed) ? 0 : parsed;
    },
    onValueChange(event) {
      this.saveDealField('value', this.parseCurrency(event.target.value));
    },
    contactFieldValue(field) {
      const contact = this.deal?.contact || {};
      if (field.custom_key)
        return contact.custom_attributes?.[field.custom_key] ?? '';

      const map = {
        contact_name: contact.name,
        contact_email: contact.email,
        contact_identifier: contact.identifier,
        contact_city: contact.additional_attributes?.city,
        contact_company: contact.additional_attributes?.company_name,
        contact_phone: contact.phone_number,
      };
      return map[field.key] ?? '';
    },
    async saveContactField(field, event) {
      const value = event.target.value;
      const contact = this.deal?.contact;
      if (!contact) return;

      const payload = {};
      if (field.custom_key) {
        payload.custom_attributes = {
          ...(contact.custom_attributes || {}),
          [field.custom_key]: value,
        };
      } else if (
        field.key === 'contact_city' ||
        field.key === 'contact_company'
      ) {
        const attribute =
          field.key === 'contact_city' ? 'city' : 'company_name';
        payload.additional_attributes = {
          ...(contact.additional_attributes || {}),
          [attribute]: value,
        };
      } else {
        const map = {
          contact_name: 'name',
          contact_email: 'email',
          contact_identifier: 'identifier',
          contact_phone: 'phone_number',
        };
        payload[map[field.key]] = value;
      }

      try {
        await ContactAPI.update(contact.id, payload);
        await this.$store.dispatch('crmDeals/fetchDeal', this.dealId);
      } catch (error) {
        useAlert('Não foi possível salvar o dado do contato');
      }
    },
    async toggleChecklistItem(entry, scope = 'stage') {
      try {
        await CrmDealsAPI.updateChecklistItem(
          this.dealId,
          entry.item,
          !entry.checked,
          scope
        );
        await this.$store.dispatch('crmDeals/fetchDeal', this.dealId);
      } catch (error) {
        useAlert('Não foi possível marcar o item');
      }
    },
    openLinkedConversation() {
      if (!this.deal?.conversation_id) return;

      this.$router.push({
        name: 'inbox_conversation',
        params: {
          accountId: this.$route.params.accountId,
          conversation_id: this.deal.conversation_id,
        },
      });
    },
    activityEmoji(type) {
      return ACTIVITY_EMOJI[type] || ACTIVITY_EMOJI.other;
    },
    activityLabel(type) {
      return ACTIVITY_LABEL[type] || ACTIVITY_LABEL.other;
    },
    // Anotação só faz sentido registrando: ela é o que já aconteceu, nunca um
    // compromisso marcado.
    availableActivityTypes() {
      return this.taskFormMode === 'log' ? ACTIVITY_TYPES : SCHEDULABLE_TYPES;
    },
    setTaskFormMode(mode) {
      this.taskFormMode = mode;
      if (mode === 'schedule' && this.newTask.activityType === NOTE_TYPE) {
        this.newTask.activityType = 'follow_up';
      }
    },
    openTaskForm(mode = 'schedule') {
      this.editingTaskId = null;
      this.taskFormMode = mode;
      this.newTask = {
        title: '',
        dueAt: mode === 'log' ? this.nowForInput() : '',
        description: '',
        activityType: mode === 'log' ? 'call' : 'follow_up',
        outcome: '',
      };
      this.showTaskForm = true;
    },
    cancelTaskForm() {
      this.editingTaskId = null;
      this.showTaskForm = false;
    },
    // Formato que o <input type="datetime-local"> aceita, no fuso local.
    nowForInput() {
      const now = new Date();
      const offset = now.getTimezoneOffset() * 60000;
      return new Date(now - offset).toISOString().slice(0, 16);
    },
    // O <input type="datetime-local"> devolve "2026-08-18T17:47", sem fuso
    // nenhum. Mandar isso cru fazia o servidor (que roda em UTC) ler como
    // 17:47 UTC, e a atividade voltava pra tela três horas mais cedo. Aqui a
    // hora vira instante de verdade antes de viajar.
    localInputToIso(value) {
      if (!value) return null;

      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
    },
    // O mesmo formulário serve pra criar e editar: com editingTaskId definido
    // ele atualiza a atividade, senão cria uma nova.
    openTaskEdit(task) {
      this.editingTaskId = task.id;
      this.taskFormMode = task.completed_at ? 'log' : 'schedule';
      this.newTask = {
        title: task.title || '',
        dueAt: task.due_at ? task.due_at.slice(0, 16) : '',
        description: task.description || '',
        activityType: task.activity_type || 'follow_up',
        outcome: task.outcome || '',
      };
      this.showTaskForm = true;
    },
    saveTask() {
      if (!this.newTask.title.trim()) return;

      const isLog = this.taskFormMode === 'log';
      const isNote = this.newTask.activityType === NOTE_TYPE;
      const happenedAt = this.newTask.dueAt || this.nowForInput();

      const task = {
        title: this.newTask.title.trim(),
        activity_type: this.newTask.activityType,
        description: this.newTask.description.trim() || null,
        outcome: isLog ? this.newTask.outcome.trim() || null : null,
        // Anotação não tem data marcada: ela vale pelo momento em que foi
        // escrita, e por isso também não aparece na agenda.
        due_at: isNote
          ? null
          : this.localInputToIso(isLog ? happenedAt : this.newTask.dueAt),
      };

      if (isLog && !this.editingTaskId) {
        task.completed_at = this.localInputToIso(happenedAt);
      }

      if (this.editingTaskId) {
        this.$store.dispatch('crmDeals/updateTask', {
          dealId: this.dealId,
          taskId: this.editingTaskId,
          task,
        });
      } else {
        this.$store.dispatch('crmDeals/createTask', {
          dealId: this.dealId,
          task,
        });
      }

      this.editingTaskId = null;
      this.showTaskForm = false;
    },
    toggleTask(task) {
      this.$store.dispatch('crmDeals/updateTask', {
        dealId: this.dealId,
        taskId: task.id,
        task: {
          completed_at: task.completed_at ? null : new Date().toISOString(),
        },
      });
    },
    removeTask(task) {
      this.$store.dispatch('crmDeals/deleteTask', {
        dealId: this.dealId,
        taskId: task.id,
      });
    },
    generateInstallmentsForDeal() {
      const count = Math.max(1, parseInt(this.installmentCount, 10) || 1);
      this.$store.dispatch('crmDeals/generateInstallments', {
        dealId: this.dealId,
        count,
        firstDueAt: this.firstInstallmentDueDate,
      });
    },
    toggleInstallmentPaid(installment) {
      this.$store.dispatch('crmDeals/updateInstallment', {
        dealId: this.dealId,
        installmentId: installment.id,
        installment: {
          paid_at: installment.paid_at ? null : new Date().toISOString(),
        },
      });
    },
    onInstallmentFieldChange(installment, field, event) {
      this.$store.dispatch('crmDeals/updateInstallment', {
        dealId: this.dealId,
        installmentId: installment.id,
        installment: { [field]: event.target.value },
      });
    },
    removeInstallment(installment) {
      this.$store.dispatch('crmDeals/deleteInstallment', {
        dealId: this.dealId,
        installmentId: installment.id,
      });
    },
    addProductLineToDeal() {
      if (!this.selectedProductId) return;
      this.$store.dispatch('crmDeals/addProductLine', {
        dealId: this.dealId,
        productLine: {
          product_id: this.selectedProductId,
          quantity: Math.max(1, parseInt(this.newLineQuantity, 10) || 1),
        },
      });
      this.selectedProductId = '';
      this.newLineQuantity = 1;
    },
    removeProductLineFromDeal(line) {
      this.$store.dispatch('crmDeals/removeProductLine', {
        dealId: this.dealId,
        lineId: line.id,
      });
    },
    formatCurrency(val) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(parseFloat(val) || 0);
    },
    formatDate(isoStr) {
      if (!isoStr) return '';
      return new Date(isoStr).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    formatDateOnly(isoStr) {
      if (!isoStr) return '';
      return new Date(isoStr).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },
    statusLabel(status) {
      if (status === 'won') return '🏆 Ganho';
      if (status === 'lost') return '❌ Perdido';
      return 'Em aberto';
    },
  },
};
</script>

<template>
  <!-- Embutido, a altura vem do conteúdo: dentro do acordeão da conversa não há
       altura definida pra ocupar, e h-full + flex-1 colapsariam o painel. -->
  <div class="flex flex-col" :class="embedded ? '' : 'h-full'">
    <!-- Drawer Header -->
    <div
      v-if="!embedded"
      class="px-6 py-4 border-b border-n-weak flex justify-between items-center"
    >
      <h2 class="text-lg font-semibold m-0 text-n-slate-12">
        Detalhes do Negócio
      </h2>
      <div class="flex items-center gap-1">
        <button
          v-tooltip.top="'Excluir negócio'"
          class="p-1 rounded hover:bg-n-ruby-3 border-none bg-transparent cursor-pointer"
          @click="confirmingDelete = true"
        >
          <span
            class="i-lucide-trash-2 w-4 h-4 text-n-slate-10 hover:text-n-ruby-11"
          />
        </button>
        <button
          class="p-1 rounded hover:bg-n-alpha-2 border-none bg-transparent cursor-pointer"
          @click="$emit('close')"
        >
          <span class="i-lucide-x w-5 h-5 text-n-slate-10" />
        </button>
      </div>
    </div>

    <div v-if="isLoading" class="flex-1 flex items-center justify-center">
      <span class="i-lucide-loader-2 w-6 h-6 animate-spin text-n-slate-9" />
    </div>

    <template v-else-if="deal">
      <!-- Confirmação de exclusão: fica logo abaixo do cabeçalho, junto da
           lixeira que a dispara. Antes ela morava no fim da aba Detalhes, e
           com o botão no topo a pessoa clicava e não via nada acontecer (e em
           outra aba ela nem renderizava). -->
      <div
        v-if="confirmingDelete"
        class="mx-4 mt-3 p-3 rounded-lg border border-n-ruby-6 bg-n-ruby-2 space-y-2"
      >
        <p class="text-sm text-n-slate-12 m-0 font-semibold">
          Excluir “{{ deal.title || deal.contact?.name }}”?
        </p>
        <p class="text-xs text-n-slate-11 m-0">
          As atividades, parcelas e produtos deste negócio vão junto, e a
          etiqueta da etapa sai da conversa. Não dá pra desfazer.
        </p>
        <div class="flex items-center gap-2 pt-1">
          <NextButton
            label="Excluir"
            size="sm"
            color="ruby"
            :is-loading="isDeleting"
            @click="deleteDeal"
          />
          <NextButton
            label="Cancelar"
            size="sm"
            variant="clear"
            @click="confirmingDelete = false"
          />
        </div>
      </div>

      <!-- Tab Bar -->
      <div class="flex border-b border-n-weak">
        <button
          v-for="tab in drawerTabs"
          :key="tab.id"
          class="flex-1 px-4 py-2.5 text-sm font-medium border-none cursor-pointer transition-colors"
          :class="
            activeTab === tab.id
              ? 'text-n-brand border-b-2 border-n-brand bg-transparent'
              : 'text-n-slate-10 hover:text-n-slate-12 bg-transparent'
          "
          @click="activeTab = tab.id"
        >
          {{ tab.label }}
          <span
            v-if="tab.badge"
            class="ml-1 text-xs bg-n-ruby-9 text-white rounded-full px-1.5 py-0.5"
            >{{ tab.badge }}</span
          >
        </button>
      </div>

      <!-- Tab Content -->
      <div :class="embedded ? '' : 'flex-1 overflow-y-auto'">
        <!-- Detalhes Tab -->
        <div v-if="activeTab === 'details'" class="px-4 py-4 space-y-4">
          <!-- Status do negócio -->
          <div v-if="!targetStatus" class="flex gap-2">
            <button
              class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer border-0"
              :class="
                deal.status === 'won'
                  ? 'bg-woot-500 text-white shadow-md'
                  : 'bg-n-blue-3 text-n-blue-11 outline outline-1 outline-n-blue-6 hover:bg-n-blue-4'
              "
              @click="startStatusChange('won')"
            >
              🏆 Ganho
            </button>
            <button
              class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer border-0"
              :class="
                deal.status === 'lost'
                  ? 'bg-n-ruby-9 text-white shadow-md'
                  : 'bg-n-ruby-3 text-n-ruby-11 outline outline-1 outline-n-ruby-6 hover:bg-n-ruby-4'
              "
              @click="startStatusChange('lost')"
            >
              ❌ Perdido
            </button>
          </div>
          <div
            v-else
            class="p-3 bg-white dark:bg-slate-800 border border-n-weak rounded-lg shadow-sm"
          >
            <label class="block text-xs font-semibold text-n-slate-11 mb-2">
              {{
                targetStatus === 'won'
                  ? 'Forma de Pagamento'
                  : 'Motivo da Perda'
              }}
            </label>
            <select
              v-if="targetStatus === 'won'"
              v-model="selectedPaymentMethodId"
              class="w-full pl-3 pr-6 py-2 border border-n-weak rounded-md text-sm bg-n-surface-1 mb-3"
            >
              <option value="" disabled>Selecione a forma de pagamento</option>
              <option v-for="m in paymentMethods" :key="m.id" :value="m.id">
                {{ m.name }}
              </option>
            </select>
            <select
              v-else
              v-model="selectedLostReasonId"
              class="w-full pl-3 pr-6 py-2 border border-n-weak rounded-md text-sm bg-n-surface-1 mb-3"
            >
              <option value="" disabled>Selecione o motivo da perda</option>
              <option v-for="r in lostReasons" :key="r.id" :value="r.id">
                {{ r.name }}
              </option>
            </select>

            <!-- Quem marca a venda nem sempre é quem vendeu: em contas onde uma
                 pessoa administra o CRM, o crédito tem que ir pro vendedor. Vem
                 preenchido com quem já é responsável, ou com quem está operando
                 quando o negócio ainda não tem ninguém. -->
            <label class="block text-xs font-semibold text-n-slate-11 mb-2">
              Responsável pela venda
            </label>
            <select
              v-model="selectedAssigneeId"
              class="w-full pl-3 pr-6 py-2 border border-n-weak rounded-md text-sm bg-n-surface-1 mb-3"
            >
              <option value="">Sem responsável</option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                {{ agent.name }}
              </option>
            </select>

            <div class="flex items-center gap-2">
              <NextButton
                label="Cancelar"
                size="sm"
                variant="faded"
                color="slate"
                class="flex-1"
                @click="cancelStatusChange"
              />
              <NextButton
                label="Confirmar"
                size="sm"
                :color="targetStatus === 'won' ? 'teal' : 'ruby'"
                class="flex-1"
                :disabled="
                  targetStatus === 'won'
                    ? !selectedPaymentMethodId
                    : !selectedLostReasonId
                "
                @click="confirmStatusChange"
              />
            </div>
          </div>

          <div class="p-3 bg-n-alpha-1 border border-n-weak rounded-lg">
            <p class="text-sm font-semibold m-0 mb-1">
              {{ statusLabel(deal.status) }}
            </p>
            <p v-if="deal.closed_at" class="text-xs text-n-slate-11 m-0">
              <span v-if="!editingClosedAt">
                em {{ formatDate(deal.closed_at) }}
                <button
                  v-if="isCurrentUserAdmin"
                  type="button"
                  class="ml-2 text-n-brand hover:underline bg-transparent border-none cursor-pointer p-0"
                  @click="startEditingClosedAt"
                >
                  editar
                </button>
              </span>
              <span v-else class="inline-flex items-center gap-2">
                Data:
                <input
                  v-model="closedAtDraft"
                  type="date"
                  min="2000-01-01"
                  class="px-2 py-1 rounded-md bg-n-background border border-n-weak text-n-slate-12"
                  @keydown.enter.prevent="saveClosedAt"
                />
                <button
                  type="button"
                  class="text-n-brand hover:underline bg-transparent border-none cursor-pointer p-0"
                  @click="saveClosedAt"
                >
                  salvar
                </button>
                <button
                  type="button"
                  class="text-n-slate-11 hover:underline bg-transparent border-none cursor-pointer p-0"
                  @click="editingClosedAt = false"
                >
                  cancelar
                </button>
              </span>
            </p>
            <p
              v-if="deal.status === 'won' && deal.payment_method_id"
              class="text-xs text-n-slate-11 m-0"
            >
              Pagamento:
              {{
                paymentMethods.find(m => m.id === deal.payment_method_id)?.name
              }}
            </p>
            <p
              v-if="deal.status === 'lost' && deal.lost_reason_id"
              class="text-xs text-n-ruby-11 m-0"
            >
              Motivo:
              {{ lostReasons.find(r => r.id === deal.lost_reason_id)?.name }}
            </p>
          </div>

          <div
            v-if="deal.status === 'won'"
            class="p-3 bg-n-surface-1 border border-n-weak rounded-lg"
          >
            <div class="flex items-center gap-3 mb-2">
              <div class="flex-1">
                <label class="block text-xs font-semibold text-n-slate-11 mb-1"
                  >Valor Pago</label
                >
                <input
                  :value="deal.paid_value"
                  type="number"
                  step="0.01"
                  :readonly="installments.length > 0"
                  class="w-full px-2 py-1.5 rounded-md text-sm bg-n-background border border-n-weak text-n-slate-12 font-semibold"
                  :class="{
                    'opacity-60 cursor-not-allowed': installments.length > 0,
                  }"
                  @change="onFieldChange('paid_value', $event)"
                />
              </div>
              <div class="flex-1">
                <label class="block text-xs font-semibold text-n-slate-11 mb-1"
                  >A Receber</label
                >
                <div
                  class="px-2 py-1.5 rounded-md text-sm font-bold"
                  :class="
                    receivable > 0
                      ? 'text-amber-500 bg-amber-500/10'
                      : 'text-emerald-500 bg-emerald-500/10'
                  "
                >
                  {{ formatCurrency(receivable) }}
                </div>
              </div>
            </div>

            <div v-if="installments.length" class="mt-2">
              <span
                class="block text-xs font-semibold text-n-slate-11 mb-2 uppercase tracking-wide"
                >Parcelas</span
              >
              <div class="flex flex-col gap-1.5">
                <div
                  v-for="p in installments"
                  :key="p.id"
                  class="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-n-weak bg-n-background text-xs"
                  :class="{ 'opacity-60': p.paid_at }"
                >
                  <button
                    v-tooltip.top="
                      p.paid_at ? 'Desmarcar paga' : 'Marcar como paga'
                    "
                    class="flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-colors"
                    :class="
                      p.paid_at
                        ? 'bg-emerald-500 border-emerald-500'
                        : 'border-n-slate-9 bg-n-surface-1 hover:border-emerald-500'
                    "
                    @click="toggleInstallmentPaid(p)"
                  >
                    <span class="text-white text-xs font-bold">{{
                      p.paid_at ? '✓' : ''
                    }}</span>
                  </button>
                  <span class="font-semibold text-n-slate-11 w-8 flex-shrink-0"
                    >{{ p.number }}/{{ installments.length }}</span
                  >
                  <input
                    :value="p.value"
                    type="number"
                    step="0.01"
                    class="w-20 px-1.5 py-1 rounded bg-n-surface-1 border border-n-weak text-n-slate-12"
                    @change="onInstallmentFieldChange(p, 'value', $event)"
                  />
                  <input
                    :value="p.due_at ? p.due_at.slice(0, 10) : ''"
                    type="date"
                    class="flex-1 px-1.5 py-1 rounded bg-n-surface-1 border border-n-weak text-n-slate-12"
                    @change="onInstallmentFieldChange(p, 'due_at', $event)"
                  />
                  <button
                    v-tooltip.top="'Excluir parcela'"
                    class="flex-shrink-0 p-1 rounded hover:bg-red-500/20 border-none bg-transparent cursor-pointer text-n-slate-10 hover:text-red-400"
                    @click="removeInstallment(p)"
                  >
                    <span class="i-lucide-trash-2 w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>

            <div class="mt-3 pt-3 border-t border-n-weak">
              <span
                class="block text-xs font-semibold text-n-slate-11 mb-2 uppercase tracking-wide"
                >{{
                  installments.length ? 'Substituir parcelamento' : 'Parcelar'
                }}</span
              >
              <div class="flex items-end gap-2">
                <div class="flex-1">
                  <label class="block text-xs text-n-slate-10 mb-1"
                    >Nº de parcelas</label
                  >
                  <input
                    v-model.number="installmentCount"
                    type="number"
                    min="1"
                    max="60"
                    class="w-full px-2 py-1.5 rounded-md text-sm bg-n-background border border-n-weak text-n-slate-12"
                  />
                </div>
                <div class="flex-1">
                  <label class="block text-xs text-n-slate-10 mb-1"
                    >1ª parcela</label
                  >
                  <input
                    v-model="firstInstallmentDueDate"
                    type="date"
                    class="w-full px-2 py-1.5 rounded-md text-sm bg-n-background border border-n-weak text-n-slate-12"
                  />
                </div>
                <NextButton
                  label="Gerar"
                  size="sm"
                  color="teal"
                  @click="generateInstallmentsForDeal"
                />
              </div>
            </div>
          </div>

          <!-- Negócio criado por automação de duplicação: aponta a origem. -->
          <p v-if="sourceDeal" class="mb-0 text-xs text-n-slate-11">
            Copiado de: {{ sourceDeal.title }}
          </p>

          <div>
            <label
              class="flex items-center gap-1 text-xs font-semibold text-n-slate-11 mb-1 uppercase tracking-wide"
              ><span class="i-lucide-type w-3 h-3" />Título</label
            >
            <input
              :value="deal.title"
              type="text"
              :placeholder="deal.contact?.name"
              class="w-full px-2 py-1.5 rounded-md text-sm bg-n-surface-1 border border-n-weak text-n-slate-12 focus:ring-2 focus:ring-n-brand font-semibold"
              @change="onFieldChange('title', $event)"
            />
          </div>

          <div>
            <label
              class="flex items-center gap-1 text-xs font-semibold text-n-slate-11 mb-1 uppercase tracking-wide"
              ><span class="i-lucide-user w-3 h-3" />Contato</label
            >
            <div v-if="!editingContact" class="space-y-1">
              <div class="flex items-center gap-2">
                <p class="text-sm text-n-slate-12 m-0 flex-1 truncate">
                  {{ deal.contact?.name || 'Sem contato' }}
                </p>
                <button
                  class="flex-shrink-0 p-1 text-n-slate-10 hover:text-n-brand hover:bg-n-brand/10 rounded transition-colors border-none bg-transparent cursor-pointer"
                  title="Trocar contato"
                  @click="startContactEdit"
                >
                  <span class="i-lucide-pencil w-3.5 h-3.5" />
                </button>
              </div>

              <!-- Antes daqui só saía o nome. Telefone, e-mail e a conversa
                   ligada já vinham do servidor e obrigavam a pessoa a sair da
                   gaveta pra ver quem era o lead.

                   Aparecem MESMO VAZIOS de propósito: escondendo, não dá pra
                   distinguir "este contato não tem telefone" de "a gaveta não
                   mostra telefone", e a segunda leitura é a que assusta. -->
              <div class="flex items-center gap-1.5 text-xs text-n-slate-11">
                <span class="i-lucide-phone w-3 h-3 flex-shrink-0" />
                <span
                  class="truncate"
                  :class="{
                    'text-n-slate-10 italic': !deal.contact?.phone_number,
                  }"
                >
                  {{ deal.contact?.phone_number || 'sem telefone' }}
                </span>
              </div>
              <div class="flex items-center gap-1.5 text-xs text-n-slate-11">
                <span class="i-lucide-mail w-3 h-3 flex-shrink-0" />
                <span
                  class="truncate"
                  :class="{ 'text-n-slate-10 italic': !deal.contact?.email }"
                >
                  {{ deal.contact?.email || 'sem e-mail' }}
                </span>
              </div>
              <button
                v-if="deal.conversation_id"
                class="flex items-center gap-1.5 text-xs text-n-brand font-medium hover:underline border-none bg-transparent cursor-pointer px-0 pt-0.5"
                @click="openLinkedConversation"
              >
                <span class="i-lucide-message-circle w-3 h-3 flex-shrink-0" />
                Abrir conversa
              </button>
            </div>
            <div v-else class="relative">
              <input
                v-model="contactQuery"
                type="text"
                autofocus
                class="w-full px-2 py-1.5 rounded-md text-sm bg-n-surface-1 border border-n-weak text-n-slate-12 focus:ring-2 focus:ring-n-brand"
                placeholder="Busque por nome ou telefone..."
                @input="onContactQueryInput"
                @keyup.esc="cancelContactEdit"
              />
              <button
                class="mt-1 text-xs text-n-slate-11 hover:text-n-slate-12 border-none bg-transparent cursor-pointer px-0"
                @click="cancelContactEdit"
              >
                Cancelar
              </button>
              <div
                v-if="contactResults.length"
                class="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto bg-n-surface-1 border border-n-weak rounded-md shadow-lg"
              >
                <button
                  v-for="contact in contactResults"
                  :key="contact.id"
                  class="w-full text-left px-3 py-2 hover:bg-n-alpha-1 border-none bg-transparent cursor-pointer"
                  @click="selectContact(contact)"
                >
                  <span class="block text-sm font-medium text-n-slate-12">{{
                    contact.name
                  }}</span>
                  <span class="block text-xs text-n-slate-10">{{
                    contact.phone_number
                  }}</span>
                </button>
              </div>
              <p
                v-else-if="isSearchingContact"
                class="text-xs text-n-slate-10 mt-1 m-0"
              >
                Buscando...
              </p>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <div class="flex-1">
              <label
                class="flex items-center gap-1 text-xs font-semibold text-n-slate-11 mb-1 uppercase tracking-wide"
                ><span class="i-lucide-funnel w-3 h-3" />Funil</label
              >
              <select
                :value="deal.funnel_id"
                class="w-full px-2 py-1.5 rounded-md text-sm bg-n-surface-1 border border-n-weak text-n-slate-12 focus:ring-2 focus:ring-n-brand cursor-pointer"
                @change="onFunnelChange($event)"
              >
                <option v-for="f in funnels" :key="f.id" :value="f.id">
                  {{ f.name }}
                </option>
              </select>
            </div>
            <div class="flex-1">
              <label
                class="flex items-center gap-1 text-xs font-semibold text-n-slate-11 mb-1 uppercase tracking-wide"
                ><span class="i-lucide-flag w-3 h-3" />Etapa</label
              >
              <select
                :value="deal.stage_id"
                class="w-full px-2 py-1.5 rounded-md text-sm bg-n-surface-1 border border-n-weak text-n-slate-12 focus:ring-2 focus:ring-n-brand cursor-pointer"
                @change="onFieldChange('stage_id', $event)"
              >
                <option v-for="s in stages" :key="s.id" :value="s.id">
                  {{ s.name }}
                </option>
              </select>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <div class="flex-1">
              <label
                class="flex items-center gap-1 text-xs font-semibold text-n-slate-11 mb-1 uppercase tracking-wide"
                ><span class="i-lucide-dollar-sign w-3 h-3" />Valor do
                Negócio</label
              >
              <input
                :value="formattedDealValue"
                type="text"
                inputmode="decimal"
                class="w-full px-2 py-1.5 rounded-md text-sm bg-n-surface-1 border border-n-weak text-n-slate-12 focus:ring-2 focus:ring-n-brand font-semibold"
                @change="onValueChange"
              />
            </div>
            <div class="flex-1">
              <label
                class="flex items-center gap-1 text-xs font-semibold text-n-slate-11 mb-1 uppercase tracking-wide"
                ><span class="i-lucide-megaphone w-3 h-3" />Origem</label
              >
              <select
                :value="deal.origin_id || ''"
                class="w-full px-2 py-1.5 rounded-md text-sm bg-n-surface-1 border border-n-weak text-n-slate-12 focus:ring-2 focus:ring-n-brand cursor-pointer"
                @change="onFieldChange('origin_id', $event)"
              >
                <option value="" disabled>Selecionar...</option>
                <option v-for="o in origins" :key="o.id" :value="o.id">
                  {{ o.name }}
                </option>
              </select>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <div class="flex-1">
              <label
                class="flex items-center gap-1 text-xs font-semibold text-n-slate-11 mb-1 uppercase tracking-wide"
                ><span class="i-lucide-user-check w-3 h-3" />Responsável</label
              >
              <select
                :value="deal.assignee_id || ''"
                class="w-full px-2 py-1.5 rounded-md text-sm bg-n-surface-1 border border-n-weak text-n-slate-12 focus:ring-2 focus:ring-n-brand cursor-pointer"
                @change="onFieldChange('assignee_id', $event)"
              >
                <option value="">Não atribuído</option>
                <option v-for="a in agents" :key="a.id" :value="a.id">
                  {{ a.name }}
                </option>
              </select>
            </div>
            <div class="flex-1">
              <label
                class="flex items-center gap-1 text-xs font-semibold text-n-slate-11 mb-1 uppercase tracking-wide"
                ><span class="i-lucide-users w-3 h-3" />Time</label
              >
              <select
                :value="deal.team_id || ''"
                class="w-full px-2 py-1.5 rounded-md text-sm bg-n-surface-1 border border-n-weak text-n-slate-12 focus:ring-2 focus:ring-n-brand cursor-pointer"
                @change="onFieldChange('team_id', $event)"
              >
                <option value="">Sem time</option>
                <option v-for="tm in teams" :key="tm.id" :value="tm.id">
                  {{ tm.name }}
                </option>
              </select>
            </div>
          </div>

          <!-- Campos personalizados do negócio (Configurações > Atributos) -->
          <div
            v-if="dealAttributeDefinitions.length"
            class="space-y-3 pt-1 border-t border-n-weak"
          >
            <div class="grid grid-cols-2 gap-3">
              <DealCustomField
                v-for="definition in visibleDealAttributes"
                :key="definition.id"
                :definition="definition"
                :value="customAttributeValue(definition)"
                @change="onCustomAttributeChange"
              />
            </div>

            <template v-if="collapsedDealAttributes.length">
              <button
                class="flex items-center gap-1.5 text-xs font-semibold text-n-slate-11 hover:text-n-slate-12 border-none bg-transparent cursor-pointer px-0"
                @click="showMoreFields = !showMoreFields"
              >
                <span
                  :class="
                    showMoreFields
                      ? 'i-lucide-chevron-down'
                      : 'i-lucide-chevron-right'
                  "
                  class="w-3.5 h-3.5"
                />
                Mais campos ({{ collapsedDealAttributes.length }})
              </button>

              <div class="grid grid-cols-2 gap-3">
                <DealCustomField
                  v-for="definition in showMoreFields
                    ? collapsedDealAttributes
                    : []"
                  :key="definition.id"
                  :definition="definition"
                  :value="customAttributeValue(definition)"
                  @change="onCustomAttributeChange"
                />
              </div>
            </template>
          </div>

          <!-- O que a etapa atual exige e ainda falta -->
          <div
            v-if="
              pendingRequirements.length ||
              stageChecklist.length ||
              pendingContactFields.length
            "
            class="p-3 rounded-md border border-n-amber-6 bg-n-amber-2 space-y-2"
          >
            <p
              class="text-xs font-semibold text-n-amber-11 m-0 uppercase tracking-wide"
            >
              Preencha antes de mover
            </p>

            <ul
              v-if="pendingRequirements.length"
              class="text-xs text-n-amber-11 m-0 pl-4 space-y-0.5"
            >
              <li v-for="item in pendingRequirements" :key="item">
                {{ item }}
              </li>
            </ul>
            <p v-else class="text-xs text-n-slate-11 m-0">
              Tudo cumprido nesta etapa.
            </p>

            <!-- Campos exigidos que moram no contato: editáveis aqui mesmo,
                 senão a pessoa leria "falta Cidade" sem ter onde preencher. -->
            <div v-if="pendingContactFields.length" class="space-y-2 pt-1">
              <div v-for="field in pendingContactFields" :key="field.key">
                <label
                  class="block text-[10px] font-semibold text-n-slate-11 mb-0.5 uppercase tracking-wide"
                >
                  Contato · {{ field.label }}
                </label>
                <select
                  v-if="field.display_type === 'list'"
                  :value="contactFieldValue(field)"
                  class="w-full px-2 py-1.5 rounded-md text-sm bg-n-surface-1 border border-n-weak text-n-slate-12"
                  @change="saveContactField(field, $event)"
                >
                  <option value="">Selecionar...</option>
                  <option
                    v-for="option in field.attribute_values || []"
                    :key="option"
                    :value="option"
                  >
                    {{ option }}
                  </option>
                </select>
                <input
                  v-else
                  :type="field.display_type === 'number' ? 'number' : 'text'"
                  :value="contactFieldValue(field)"
                  class="w-full px-2 py-1.5 rounded-md text-sm bg-n-surface-1 border border-n-weak text-n-slate-12"
                  @change="saveContactField(field, $event)"
                />
              </div>
            </div>

            <div v-if="stageChecklist.length" class="space-y-1 pt-1">
              <label
                v-for="entry in stageChecklist"
                :key="entry.item"
                class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
              >
                <input
                  type="checkbox"
                  :checked="entry.checked"
                  class="cursor-pointer"
                  @change="toggleChecklistItem(entry)"
                />
                <span
                  :class="entry.checked ? 'line-through text-n-slate-10' : ''"
                >
                  {{ entry.item }}
                </span>
              </label>
            </div>
          </div>

          <!-- O que o funil exige para marcar como ganho e ainda falta -->
          <div
            v-if="showWinBlock"
            class="p-3 rounded-md border border-n-teal-6 bg-n-teal-2 space-y-2"
          >
            <p
              class="text-xs font-semibold text-n-teal-11 m-0 uppercase tracking-wide"
            >
              🏆 Para marcar como ganho
            </p>

            <ul
              v-if="pendingWinRequirements.length"
              class="text-xs text-n-teal-11 m-0 pl-4 space-y-0.5"
            >
              <li v-for="item in pendingWinRequirements" :key="item">
                {{ item }}
              </li>
            </ul>
            <p v-else class="text-xs text-n-slate-11 m-0">
              Tudo cumprido para o ganho.
            </p>

            <div v-if="winChecklist.length" class="space-y-1 pt-1">
              <label
                v-for="entry in winChecklist"
                :key="`win-${entry.item}`"
                class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
              >
                <input
                  type="checkbox"
                  :checked="entry.checked"
                  class="cursor-pointer"
                  @change="toggleChecklistItem(entry, 'win')"
                />
                <span
                  :class="entry.checked ? 'line-through text-n-slate-10' : ''"
                >
                  {{ entry.item }}
                </span>
              </label>
            </div>
          </div>
        </div>

        <!-- Atividades Tab -->
        <div
          v-if="activeTab === 'tasks'"
          class="flex flex-col"
          :class="embedded ? '' : 'h-full'"
        >
          <div v-if="showTaskForm" class="p-4 border-b border-n-weak space-y-3">
            <div class="flex gap-1 p-1 rounded-md bg-n-alpha-1">
              <button
                v-for="mode in [
                  { id: 'schedule', label: 'Agendar' },
                  { id: 'log', label: 'Registrar' },
                ]"
                :key="mode.id"
                class="flex-1 px-3 py-1.5 text-sm font-medium rounded border-none cursor-pointer transition-colors"
                :class="
                  taskFormMode === mode.id
                    ? 'bg-n-surface-1 text-n-slate-12 shadow-sm'
                    : 'bg-transparent text-n-slate-11 hover:text-n-slate-12'
                "
                @click="setTaskFormMode(mode.id)"
              >
                {{ mode.label }}
              </button>
            </div>

            <input
              v-model="newTask.title"
              type="text"
              :placeholder="
                taskFormMode === 'log'
                  ? 'O que foi feito...'
                  : 'O que precisa ser feito...'
              "
              class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-surface-1 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />

            <select
              v-model="newTask.activityType"
              class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-surface-1 text-sm"
            >
              <option
                v-for="type in availableActivityTypes()"
                :key="type.value"
                :value="type.value"
              >
                {{ type.emoji }} {{ type.label }}
              </option>
            </select>

            <input
              v-if="newTask.activityType !== 'note'"
              v-model="newTask.dueAt"
              type="datetime-local"
              class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-surface-1 text-sm"
            />

            <textarea
              v-model="newTask.description"
              placeholder="Observações (opcional)..."
              rows="2"
              class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-surface-1 text-sm resize-none"
            />

            <textarea
              v-if="taskFormMode === 'log'"
              v-model="newTask.outcome"
              placeholder="O que aconteceu? (resultado)"
              rows="2"
              class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-surface-1 text-sm resize-none"
            />

            <div class="flex gap-2 justify-end">
              <button
                class="px-3 py-1.5 text-sm font-medium rounded-md border border-n-weak bg-transparent text-n-slate-11 hover:bg-n-alpha-1 cursor-pointer"
                @click="cancelTaskForm"
              >
                Cancelar
              </button>
              <button
                class="button success"
                :disabled="!newTask.title.trim()"
                @click="saveTask"
              >
                {{
                  editingTaskId
                    ? 'Salvar'
                    : taskFormMode === 'log'
                      ? 'Registrar'
                      : 'Agendar'
                }}
              </button>
            </div>
          </div>

          <div class="flex-1 overflow-y-auto">
            <div
              v-if="tasks.length === 0"
              class="flex flex-col items-center justify-center py-12 text-n-slate-10"
            >
              <span class="text-4xl mb-3">📋</span>
              <p class="text-sm m-0">Nenhuma atividade registrada</p>
            </div>
            <div v-else class="divide-y divide-n-weak">
              <div
                v-for="task in tasks"
                :key="task.id"
                class="flex items-start gap-3 px-4 py-3 group"
                :class="{ 'opacity-50': task.completed_at }"
              >
                <button
                  v-tooltip.top="
                    task.completed_at
                      ? 'Reabrir atividade'
                      : 'Concluir atividade'
                  "
                  class="mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-all"
                  :class="
                    task.completed_at
                      ? 'bg-n-blue-9 border-n-blue-9'
                      : 'border-n-slate-9 bg-n-surface-1 hover:bg-n-blue-3 hover:border-n-blue-9'
                  "
                  @click="toggleTask(task)"
                >
                  <span
                    v-if="task.completed_at"
                    class="text-white text-xs font-bold"
                    >✓</span
                  >
                </button>
                <div class="flex-1 min-w-0">
                  <span
                    class="text-sm font-medium truncate block"
                    :class="
                      task.completed_at
                        ? 'line-through text-n-slate-10'
                        : 'text-n-slate-12'
                    "
                  >
                    {{ activityEmoji(task.activity_type) }} {{ task.title }}
                  </span>
                  <span class="text-xs text-n-slate-10">
                    {{ activityLabel(task.activity_type)
                    }}<template v-if="task.due_at">
                      · {{ formatDate(task.due_at) }}</template
                    >
                  </span>
                  <p
                    v-if="task.description"
                    class="text-xs text-n-slate-10 m-0 mt-1"
                  >
                    {{ task.description }}
                  </p>
                  <p
                    v-if="task.outcome"
                    class="text-xs text-n-slate-11 m-0 mt-1 pl-2 border-l-2 border-n-brand/40"
                  >
                    {{ task.outcome }}
                  </p>
                </div>
                <div
                  class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <button
                    class="p-1 rounded hover:bg-n-brand/10 border-none bg-transparent cursor-pointer text-n-slate-10 hover:text-n-brand"
                    title="Editar"
                    @click="openTaskEdit(task)"
                  >
                    <span class="i-lucide-pencil w-3.5 h-3.5" />
                  </button>
                  <button
                    class="p-1 rounded hover:bg-red-500/20 border-none bg-transparent cursor-pointer text-n-slate-10 hover:text-red-400"
                    title="Excluir"
                    @click="removeTask(task)"
                  >
                    <span class="i-lucide-trash-2 w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="!showTaskForm"
            class="p-3 border-t border-n-weak flex gap-2"
          >
            <button
              class="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-white rounded-md border-none cursor-pointer bg-woot-500 hover:bg-woot-600"
              @click="openTaskForm('schedule')"
            >
              <span class="i-lucide-calendar-plus w-4 h-4" />
              Agendar
            </button>
            <button
              class="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-n-weak bg-transparent text-n-slate-11 hover:bg-n-alpha-1 cursor-pointer"
              @click="openTaskForm('log')"
            >
              <span class="i-lucide-check-circle-2 w-4 h-4" />
              Registrar
            </button>
          </div>
        </div>

        <!-- Documentos Tab -->
        <DealDocuments
          v-if="activeTab === 'documents'"
          :deal-id="dealId"
          @changed="$store.dispatch('crmDeals/fetchDeal', dealId)"
        />

        <!-- Produtos Tab -->
        <div
          v-if="activeTab === 'products'"
          class="flex flex-col"
          :class="embedded ? '' : 'h-full'"
        >
          <div class="flex-1 overflow-y-auto">
            <div
              v-if="productLines.length === 0"
              class="flex flex-col items-center justify-center py-12 text-n-slate-10"
            >
              <span class="text-4xl mb-3">📦</span>
              <p class="text-sm m-0">Nenhum produto associado</p>
            </div>
            <div v-else class="divide-y divide-n-weak">
              <div
                v-for="line in productLines"
                :key="line.id"
                class="px-4 py-3 flex items-center justify-between group"
              >
                <div class="min-w-0">
                  <span class="text-sm font-semibold text-n-slate-12">{{
                    line.product_name
                  }}</span>
                  <span class="text-xs text-n-slate-10 ml-2"
                    >x{{ line.quantity }}</span
                  >
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-xs font-bold text-emerald-500">{{
                    formatCurrency(line.total)
                  }}</span>
                  <button
                    class="p-1 rounded hover:bg-red-500/20 border-none bg-transparent cursor-pointer text-n-slate-10 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Remover"
                    @click="removeProductLineFromDeal(line)"
                  >
                    <span class="i-lucide-trash-2 w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Adicionar produto -->
          <div class="p-3 border-t border-n-weak space-y-2">
            <div class="flex gap-2">
              <select
                v-model="selectedProductId"
                class="flex-1 pl-3 pr-6 py-2 border border-n-weak rounded-md bg-n-surface-1 text-sm"
              >
                <option value="">Selecione um produto...</option>
                <option
                  v-for="product in availableProducts"
                  :key="product.id"
                  :value="product.id"
                >
                  {{ product.name }} —
                  {{ formatCurrency(product.default_price) }}
                </option>
              </select>
              <input
                v-model.number="newLineQuantity"
                type="number"
                min="1"
                class="w-16 px-2 py-2 border border-n-weak rounded-md bg-n-surface-1 text-sm text-center"
              />
            </div>
            <button
              class="button success w-full"
              :disabled="!selectedProductId"
              @click="addProductLineToDeal"
            >
              + Adicionar Produto
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
